// Students may keep the Wall of Fame open for a limited time per day (10
// minutes by default, set on the server). While the wall is open and the tab
// is visible the page sends a heartbeat every 15 seconds; the server measures
// the time between heartbeats and answers with today's time left, which the
// badge counts down between heartbeats. When it reaches zero the wall closes
// until midnight (Indian time) and shows WtfTimeUpScreen instead.
//
// If the time can't be loaded (e.g. the server is unreachable) the wall opens
// as before, without a timer.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Hourglass } from "lucide-react";
import { sendWtfViewTimeHeartbeat } from "../../api";

const HEARTBEAT_MS = 15000;

const isVisible = () =>
  typeof document === "undefined" || document.visibilityState !== "hidden";

// One id per open wall (page visit). The server only lets the wall that sent
// the last heartbeat stop the clock: React mounts the wall twice in
// development, and the first mount's "stop" reaches the server after the
// second mount's "start", which used to pause the clock while the wall was
// open (the countdown then jumped back up at the next heartbeat).
const newSessionId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

// The server answers in whole seconds, a moment after it measured, so its
// figure can sit up to a second or so above the running countdown. Smaller
// differences than this keep the countdown as it is instead of ticking back up.
const ROUNDING_SLACK_SECONDS = 2;

export function useWtfDailyTime(enabled) {
  const [state, setState] = useState(() => ({
    ready: !enabled,
    limited: false,
    timeUp: false,
    limitSeconds: 0,
    remainingSeconds: 0,
    resetsAt: null,
  }));
  // Countdown base: seconds left at `at` (Date.now()); null while paused.
  const baseRef = useRef({ remaining: 0, at: null });
  const timeUpRef = useRef(false);
  const busyRef = useRef(false);
  const openRef = useRef(false);
  const sessionRef = useRef(null);
  // False until this wall has shown a time, so its first answer is taken as is.
  const shownRef = useRef(false);

  const apply = useCallback((data) => {
    if (!data?.limited) {
      timeUpRef.current = false;
      setState((prev) => ({ ...prev, ready: true, limited: false, timeUp: false }));
      return;
    }
    timeUpRef.current = Boolean(data.timeUp);
    let remaining = data.remainingSeconds;
    if (shownRef.current && !data.timeUp) {
      const { remaining: base, at } = baseRef.current;
      const shown = at ? Math.max(0, base - (Date.now() - at) / 1000) : base;
      if (remaining > shown && remaining - shown < ROUNDING_SLACK_SECONDS) {
        remaining = shown;
      }
    }
    shownRef.current = true;
    baseRef.current = {
      remaining,
      at: data.timeUp || !openRef.current ? null : Date.now(),
    };
    setState({
      ready: true,
      limited: true,
      timeUp: Boolean(data.timeUp),
      limitSeconds: data.limitSeconds,
      remainingSeconds: remaining,
      resetsAt: data.resetsAt || null,
    });
  }, []);

  const send = useCallback(
    async (action) => {
      if (busyRef.current && action === "tick") return;
      busyRef.current = true;
      try {
        const response = await sendWtfViewTimeHeartbeat(
          action,
          sessionRef.current,
        );
        if (response?.success) apply(response.data);
        else setState((prev) => (prev.ready ? prev : { ...prev, ready: true }));
      } catch (error) {
        // Fail open: the wall works as before when the time can't be loaded.
        setState((prev) => (prev.ready ? prev : { ...prev, ready: true }));
      } finally {
        busyRef.current = false;
      }
    },
    [apply],
  );

  // Open: start counting; close (unmount): stop. Hidden tabs don't count.
  useEffect(() => {
    if (!enabled) return undefined;
    const sessionId = newSessionId();
    sessionRef.current = sessionId;
    openRef.current = isVisible();
    send(openRef.current ? "start" : "stop");

    const onVisibility = () => {
      if (timeUpRef.current) return;
      if (isVisible()) {
        openRef.current = true;
        send("start");
      } else {
        // Freeze the countdown where it is while the tab is hidden.
        const { remaining, at } = baseRef.current;
        if (at) {
          baseRef.current = {
            remaining: Math.max(0, remaining - (Date.now() - at) / 1000),
            at: null,
          };
        }
        openRef.current = false;
        send("stop");
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    const heartbeat = setInterval(() => {
      if (openRef.current && !timeUpRef.current) send("tick");
    }, HEARTBEAT_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(heartbeat);
      if (openRef.current && !timeUpRef.current) {
        // Fire and forget; nothing may throw while unmounting. Sent with this
        // wall's own id, so it can't pause a wall that has replaced it.
        Promise.resolve()
          .then(() => sendWtfViewTimeHeartbeat("stop", sessionId))
          .catch(() => {});
      }
      openRef.current = false;
    };
  }, [enabled, send]);

  // Count down every second; at zero ask the server, which closes the wall.
  useEffect(() => {
    if (!enabled || !state.limited || state.timeUp) return undefined;
    const timer = setInterval(() => {
      const { remaining, at } = baseRef.current;
      if (!at) return;
      const left = Math.max(0, remaining - (Date.now() - at) / 1000);
      setState((prev) => ({ ...prev, remainingSeconds: left }));
      if (left <= 0) send("tick");
    }, 1000);
    return () => clearInterval(timer);
  }, [enabled, state.limited, state.timeUp, send]);

  // Closed for today: open again after midnight.
  useEffect(() => {
    if (!enabled || !state.timeUp || !state.resetsAt) return undefined;
    const wait = new Date(state.resetsAt).getTime() - Date.now() + 2000;
    const timer = setTimeout(
      () => {
        openRef.current = isVisible();
        timeUpRef.current = false;
        send(openRef.current ? "start" : "stop");
      },
      Math.min(Math.max(wait, 1000), 2147483000),
    );
    return () => clearTimeout(timer);
  }, [enabled, state.timeUp, state.resetsAt, send]);

  return state;
}

const formatClock = (seconds) => {
  const total = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const spokenTime = (seconds) => {
  const total = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  const parts = [];
  if (minutes) parts.push(`${minutes} minute${minutes === 1 ? "" : "s"}`);
  if (secs || !minutes) parts.push(`${secs} second${secs === 1 ? "" : "s"}`);
  return parts.join(" ");
};

// "12 h 5 min", "45 min" or "less than a minute" until `resetsAt`.
export const timeUntil = (resetsAt, now = Date.now()) => {
  const ms = new Date(resetsAt).getTime() - now;
  if (!Number.isFinite(ms) || ms <= 60000) return "less than a minute";
  const totalMinutes = Math.ceil(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (!hours) return `${minutes} min`;
  return minutes ? `${hours} h ${minutes} min` : `${hours} h`;
};

// Time left today, pinned to the top-left of the wall while it scrolls.
export function WtfTimeLeftBadge({ remainingSeconds, limitSeconds }) {
  const lastMinute = remainingSeconds <= 60;
  const lowTime = remainingSeconds <= 180;
  const percent = limitSeconds
    ? Math.max(0, Math.min(100, (remainingSeconds / limitSeconds) * 100))
    : 0;
  const tone = lastMinute
    ? { box: "border-red-300 text-red-700", bar: "bg-red-500", icon: "bg-red-100" }
    : lowTime
      ? { box: "border-amber-300 text-amber-800", bar: "bg-amber-500", icon: "bg-amber-100" }
      : { box: "border-emerald-300 text-emerald-800", bar: "bg-emerald-500", icon: "bg-emerald-100" };

  return (
    <div
      role="timer"
      aria-live="off"
      aria-label={`Wall of Fame time left today: ${spokenTime(remainingSeconds)}`}
      data-testid="wtf-time-left"
      className={`inline-flex items-center gap-2.5 rounded-2xl border-2 bg-white/95 pl-2 pr-4 py-1.5 shadow-lg ${tone.box} ${
        lastMinute ? "animate-pulse" : ""
      }`}
    >
      <span
        className={`flex h-9 w-9 flex-none items-center justify-center rounded-full ${tone.icon}`}
        aria-hidden="true"
      >
        <Hourglass className="h-5 w-5" />
      </span>
      <span className="flex flex-col leading-tight">
        <span className="text-[11px] font-bold uppercase tracking-wide opacity-80">
          {lastMinute ? "Last minute!" : "WTF time left"}
        </span>
        <span className="text-xl font-extrabold tabular-nums">
          {formatClock(remainingSeconds)}
        </span>
        <span className="mt-1 block h-1.5 w-24 overflow-hidden rounded-full bg-gray-200">
          <span
            className={`block h-full rounded-full transition-[width] duration-1000 ${tone.bar}`}
            style={{ width: `${percent}%` }}
          />
        </span>
      </span>
    </div>
  );
}

// Shown instead of the wall once today's time is used up.
export function WtfTimeUpScreen({
  limitSeconds,
  resetsAt,
  onContinue,
  autoHeight = false,
  style,
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);
  const minutes = Math.round((limitSeconds || 600) / 60);

  return (
    <div
      className={`flex w-full items-center justify-center p-6 ${
        autoHeight ? "flex-1 min-h-[520px]" : "h-screen"
      }`}
      style={style}
      data-wtf-section="true"
      data-testid="wtf-time-up"
    >
      <div className="flex w-full max-w-lg flex-col items-center text-center">
        <img
          src="/mywalloffamemain.png"
          alt="My Wall of FAME"
          className="mb-4 w-[300px] max-w-full drop-shadow-lg"
        />
        <div className="w-full rounded-3xl bg-white/95 px-8 py-8 shadow-xl">
          <div className="text-6xl" aria-hidden="true">
            ⏰
          </div>
          <h2 className="mt-3 text-2xl font-extrabold text-gray-900">
            WTF time is over for today
          </h2>
          <p className="mt-2 text-base text-gray-600">
            You have used your {minutes} minutes of Wall of Fame for today.
            Come back tomorrow to see more amazing work!
          </p>
          {resetsAt && (
            <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-2 text-sm font-bold text-amber-800 border border-amber-200">
              <Hourglass className="h-4 w-4" aria-hidden="true" />
              Opens again in {timeUntil(resetsAt, now)}
            </p>
          )}
          {onContinue && (
            <div className="mt-6">
              <button
                type="button"
                onClick={onContinue}
                className="rounded-full bg-purple-600 px-6 py-3 text-base font-bold text-white shadow-md hover:bg-purple-700 transition-colors"
              >
                Continue learning
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
